import {useCallback, useEffect, useRef, useState} from "react";
import BannerTag from "../BannerTag/BannerTag";
import styles from "./HeroBanner.module.css";

const AUTO_PLAY_MS = 6000;
const CDN_BASE = "https://images.cdn.raspaygana.com.ve/images";

/**
 * Shared page hero banner with optional multi-slide support.
 *
 * @param {object} props
 * @param {Array<{content: import("react").ReactNode, bgImage?: string}>} props.slides
 *   Each item has a `content` node and optional `bgImage` (CDN key or full URL).
 * @param {boolean} [props.autoPlay=true] - Auto-rotate slides.
 * @param {number} [props.interval=6000] - Auto-rotate interval in ms.
 */
const HeroBanner = ({slides, autoPlay = true, interval = AUTO_PLAY_MS}) => {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const count = slides.length;
  const multi = count > 1;

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoPlay && multi) {
      timerRef.current = setInterval(() => {
        setActive(prev => (prev + 1) % count);
      }, interval);
    }
  }, [autoPlay, multi, count, interval]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const goTo = useCallback(
    idx => {
      setActive(idx);
      resetTimer();
    },
    [resetTimer]
  );

  const resolveImageUrl = key => {
    if (!key) return null;
    if (key.startsWith("http") || key.startsWith("/") || key.startsWith("data:")) return key;
    return `${CDN_BASE}/${key}`;
  };

  return (
    <section className={styles.banner}>
      <div className={styles.viewport}>
        <div
          className={styles.track}
          style={{transform: `translateX(-${active * 100}%)`}}
        >
          {slides.map((slide, i) => {
            const isLegacy = !slide.content && !slide.bgImage;
            const content = isLegacy ? slide : slide.content;
            const bgImage = isLegacy ? null : resolveImageUrl(slide.bgImage);
            const hasBg = !!bgImage;

            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: static ordered slides
                key={`slide-${i}`}
                className={`${styles.slide} ${hasBg ? styles.slideWithImage : ""}`}
                aria-hidden={i !== active}
              >
                {hasBg && (
                  <>
                    <img
                      className={styles.slideBgImage}
                      src={bgImage}
                      alt=""
                      loading={i === 0 ? "eager" : "lazy"}
                      draggable={false}
                    />
                    <div className={styles.slideScrim} />
                  </>
                )}
                {content}
              </div>
            );
          })}
        </div>
      </div>

      {multi && (
        <div className={styles.dots}>
          {slides.map((_, i) => (
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: dot indicators
              key={`dot-${i}`}
              type="button"
              className={`${styles.dot} ${i === active ? styles.dotActive : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      <div className={styles.glow} />
      <div className={styles.glowSecondary} />
    </section>
  );
};

export default HeroBanner;

/* ── Re-export sub-parts for slide composition ── */
export const HeroContent = ({children}) => <div className={styles.content}>{children}</div>;
export const HeroTitle = ({children}) => <h1 className={styles.title}>{children}</h1>;
export const HeroAccent = ({children}) => <span className={styles.accent}>{children}</span>;
export const HeroSubtitle = ({children}) => <h2 className={styles.subtitle}>{children}</h2>;
export const HeroStats = ({children}) => <div className={styles.stats}>{children}</div>;
export const HeroStat = ({value, label}) => (
  <div className={styles.stat}>
    <span className={styles.statValue}>{value}</span>
    <span className={styles.statLabel}>{label}</span>
  </div>
);
export const HeroTag = ({variant, label}) => (
  <BannerTag
    variant={variant}
    label={label}
    className={styles.tag}
  />
);
export const HeroCta = ({label, href, onClick}) => {
  if (href) {
    return (
      <a
        className={styles.cta}
        href={href}
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={styles.cta}
      onClick={onClick}
    >
      {label}
    </button>
  );
};
